import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useUser } from '../contexts/UserContext';
import { Loader, CheckCircle, AlertCircle } from 'lucide-react';
import BackButton from '../components/BackButton';

export default function FeedbackPage() {
  const { user } = useUser();
  const [form, setForm] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Reset form state when component mounts
    setForm({});
    setSuccess(false);
    setError(null);
  }, []);

  const handleChange = (key: string, value: string) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const handleCheckboxChange = (key: string, value: string, checked: boolean) => {
    const prev = form[key] ? form[key].split(',') : [];
    const newValues = checked 
      ? [...prev, value] 
      : prev.filter(v => v !== value);
    
    handleChange(key, newValues.join(','));
  };

  const handleSubmit = async () => {
    if (!user) {
      setError('Du bist nicht angemeldet. Bitte melde dich an, um Feedback zu geben.');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      // Include the user ID in the data being inserted
      const { error: insertError } = await supabase
        .from('feedback')
        .insert([{ 
          ...form, 
          user_id: user.id 
        }]);
      
      if (insertError) {
        console.error('Fehler beim Speichern:', insertError);
        setError('Fehler beim Speichern: ' + insertError.message);
      } else {
        setSuccess(true);
        setForm({});
        
        // Reset success message after 3 seconds
        setTimeout(() => {
          setSuccess(false);
        }, 3000);
      }
    } catch (error: any) {
      console.error('Unexpected error:', error);
      setError('Ein unerwarteter Fehler ist aufgetreten: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const questions = [
    "Wie gefällt dir das Design auf den ersten Blick?",
    "Wie intuitiv ist die Startseite aufgebaut? (1 = gar nicht, 10 = sehr intuitiv)",
    "Was denkst du, worum es bei Meet&Eat geht (nur anhand der Startseite)?",
    "Welche Elemente wirken unklar oder unruhig?",
    "Wie leicht findest du dich in der App zurecht? (1 = überhaupt nicht, 10 = sehr leicht)",
    "Welche Funktionen hast du auf Anhieb gefunden?",
    "Hattest du das Gefühl, irgendwo festzustecken oder nicht weiterzukommen? Wenn ja, wo?",
    "Ist die Menüführung logisch aufgebaut? (1 = gar nicht, 10 = sehr logisch)",
    "Wie einfach war die Registrierung/Login? (1 = sehr schwer, 10 = sehr einfach)",
    "Ist die Seite übersichtlich genug? Wo wären Verbesserungen möglich",
    "Wie vertrauenswürdig wirkt die App auf dich bezüglich Datenschutz? (1 = gar nicht, 10 = sehr vertrauenswürdig)",
    "Welche Funktionen hast du getestet?",
    "Haben alle getesteten Funktionen einwandfrei funktioniert? Falls nein, was genau?",
    "Wie hilfreich sind die Filter- und Suchoptionen? (1 = gar nicht, 10 = sehr hilfreich)",
    "Welche Funktionen haben dich besonders überzeugt?",
    "Welche Features vermisst du aktuell?",
    "Wie empfindest du die Texte und Beschreibungen in der App?",
    "Welche Begriffe waren dir unklar?",
    "Hast du Verbesserungsvorschläge für Formulierungen oder Labels?",
    "Wie sehr hattest du das Gefühl, du möchtest etwas ausprobieren oder mitmachen? (1–10)",
    "Was motiviert dich an Meet&Eat – wenn überhaupt?",
    "Hattest du beim Testen ein positives oder negatives Gefühl? (1 sehr negativ –10 sehr positiv)",
    "Würdest du jemandem die App zum Testen empfehlen?",
    "Wie schnell lädt die App auf deinem Gerät? (1–10)",
    "Hast du Fehler, Bugs oder seltsames Verhalten erlebt? Wenn ja, was genau?",
    "Auf welchem Gerät hast du getestet?",
    "Was war dein Lieblingsmoment beim Testen?",
    "Was hat dich am meisten gestört?",
    "Wie wahrscheinlich ist es, dass du Meet&Eat nach dem Launch nutzt? (1–10)",
    "Sonstige Anmerkungen, Ideen oder Wünsche"
  ];

  const checkboxQuestions: Record<number, string[]> = {
    6: ['Suche', 'Events', 'Blind Dates', 'Leute entdecken', 'Chat', 'Restaurant-Infos', 'Profil'],
    12: ['Suchen', 'Blind Date erstellen', 'Dinner planen', 'Restaurant eintragen', 'Leute entdecken', 'Chat'],
    17: ['Sehr angenehm', 'Ok', 'Etwas technisch', 'Nicht passend'],
    23: ['Ja', 'Nein', 'Vielleicht'],
    26: ['Android Smartphone', 'iPhone', 'Tablet', 'PC/Laptop', 'Sonstiges']
  };

  const sliderQuestions = [1, 4, 7, 8, 10, 13, 19, 21, 23, 28];

  return (
    <div className="max-w-4xl mx-auto p-4">
      <div className="flex items-center space-x-4 mb-8">
        <BackButton />
        <h1 className="text-2xl font-bold text-gray-900">Meet&Eat Feedback Fragebogen</h1>
      </div>

      {!user && (
        <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 mb-4">
          <p className="font-bold">Hinweis:</p>
          <p>Du bist nicht angemeldet. Bitte melde dich an, um Feedback zu geben.</p>
        </div>
      )}

      {error && (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4 flex items-center">
          <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
          <p>{error}</p>
        </div>
      )}

      {success && (
        <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-4 flex items-center">
          <CheckCircle className="h-5 w-5 mr-2 flex-shrink-0" />
          <p>Vielen Dank für dein Feedback! Deine Antworten wurden erfolgreich gespeichert.</p>
        </div>
      )}

      <div className="space-y-6 bg-white rounded-lg shadow p-6">
        {questions.map((text, i) => {
          const qNum = i + 1;
          if (sliderQuestions.includes(i)) {
            return (
              <div key={qNum} className="border-b pb-6">
                <label className="font-semibold block mb-2">{qNum}. {text}</label>
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-500">1</span>
                  <input 
                    type="range" 
                    min="1" 
                    max="10" 
                    defaultValue="5"
                    onChange={e => handleChange(`q${qNum}`, e.target.value)} 
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-orange-500" 
                  />
                  <span className="text-sm text-gray-500">10</span>
                </div>
                <div className="text-sm text-gray-500 mt-1 text-center">
                  Aktueller Wert: {form[`q${qNum}`] || '5'}
                </div>
              </div>
            );
          } else if (checkboxQuestions[qNum]) {
            return (
              <div key={qNum} className="border-b pb-6">
                <label className="font-semibold block mb-2">{qNum}. {text}</label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {checkboxQuestions[qNum].map((label, idx) => (
                    <div key={idx} className="flex items-center">
                      <input 
                        type="checkbox" 
                        id={`q${qNum}_${idx}`} 
                        value={label}
                        onChange={e => handleCheckboxChange(`q${qNum}`, label, e.target.checked)} 
                        className="rounded text-orange-500 focus:ring-orange-500 mr-2"
                      />
                      <label htmlFor={`q${qNum}_${idx}`} className="text-gray-700">{label}</label>
                    </div>
                  ))}
                </div>
              </div>
            );
          } else {
            return (
              <div key={qNum} className="border-b pb-6">
                <label className="font-semibold block mb-2">{qNum}. {text}</label>
                <textarea 
                  className="w-full p-2 border rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500" 
                  rows={3}
                  onChange={e => handleChange(`q${qNum}`, e.target.value)}
                  value={form[`q${qNum}`] || ''}
                ></textarea>
              </div>
            );
          }
        })}

        <button
          onClick={handleSubmit}
          disabled={loading || !user}
          className={`${
            loading || !user ? 'bg-gray-400 cursor-not-allowed' : 'bg-orange-500 hover:bg-orange-600'
          } text-white px-6 py-3 rounded-lg transition-colors flex items-center justify-center`}
        >
          {loading ? (
            <>
              <Loader className="h-5 w-5 animate-spin mr-2" />
              Wird gesendet...
            </>
          ) : (
            'Feedback absenden'
          )}
        </button>
      </div>
    </div>
  );
}